import { createHash, randomUUID } from 'node:crypto'
import { mkdir, readFile, rename, rm, stat, writeFile } from 'node:fs/promises'
import { dirname, join, resolve } from 'node:path'
import { setTimeout as delay } from 'node:timers/promises'
import type { Context } from '@tnega/core'
import {
  ArtifactError,
  ArtifactStore,
  MAX_ARTIFACT_BYTES,
  type ArtifactPutRequest,
  type ArtifactRef,
} from '@tnega/artifact-store'

export interface LocalArtifactConfig {
  /**
   * 产物目录。组合层决定它在 Project 目录下的位置（约定为
   * `<project>/.tnega/projects/<projectId>/artifacts`）。
   */
  root: string
  /** 单份内容上限，默认 {@link MAX_ARTIFACT_BYTES}。 */
  maxBytes?: number
}

function toBytes(content: string | Uint8Array): Uint8Array {
  return typeof content === 'string' ? new TextEncoder().encode(content) : content
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Windows 上，目标正被另一个写入者替换时 `rename` 会短暂失败；内容寻址让这次写入
 * 可以幂等重试，不必让并发发布的一方报错。
 */
const RETRYABLE_RENAME_CODES = new Set(['EACCES', 'EBUSY', 'EPERM'])
const RENAME_RETRIES = 6

async function isFile(path: string): Promise<boolean> {
  try {
    return (await stat(path)).isFile()
  } catch {
    return false
  }
}

/**
 * 本地产物 Provider：内容寻址的文件树。
 *
 * ```
 * <root>/<hash 前两位>/<hash>            # 内容
 * <root>/<hash 前两位>/<hash>.meta.json  # { size, mediaType }
 * ```
 *
 * 两级目录只是为了让一个目录别堆上万个文件，`<hash 前两位>` 不参与寻址语义。
 *
 * 取舍：
 *
 * - **写一次，不改**。内容按哈希落盘后不再变化；`mediaType` 是内容附带的第一份声明，
 *   同内容再 `put` 一次不会改掉它 —— 引用必须稳定，否则同一个哈希会解释出两种类型。
 * - **先写临时文件再改名**。崩在写入中间只会留下一个 `.tmp`，读者永远看不到半份内容。
 *   并发写同一哈希是幂等的：另一写入者已经落下同一份内容时，这次写入算成功。
 */
export class LocalArtifactStore extends ArtifactStore {
  private readonly root: string
  private readonly maxBytes: number

  constructor(ctx: Context, config: LocalArtifactConfig) {
    super(ctx)
    if (!config?.root || typeof config.root !== 'string') {
      throw new ArtifactError('artifact-local requires a root directory', 'ARTIFACT_INVALID')
    }
    const maxBytes = config.maxBytes ?? MAX_ARTIFACT_BYTES
    if (!Number.isSafeInteger(maxBytes) || maxBytes < 1) {
      throw new ArtifactError('maxBytes must be a positive integer', 'ARTIFACT_INVALID')
    }
    this.root = resolve(config.root)
    this.maxBytes = maxBytes
  }

  protected override async runPut(request: Required<ArtifactPutRequest>): Promise<ArtifactRef> {
    const bytes = toBytes(request.content)
    if (!bytes.byteLength) {
      throw new ArtifactError('artifact content must not be empty', 'ARTIFACT_INVALID')
    }
    if (bytes.byteLength > this.maxBytes) {
      throw new ArtifactError(
        `artifact content exceeds ${this.maxBytes} bytes`,
        'ARTIFACT_INVALID',
      )
    }
    const hash = createHash('sha256').update(bytes).digest('hex')
    const existing = await this.runStat(hash)
    if (existing) return existing
    const file = join(this.contentDir(hash), hash)
    await mkdir(dirname(file), { recursive: true })
    await this.writeAtomic(file, bytes)
    await this.writeAtomic(
      `${file}.meta.json`,
      new TextEncoder().encode(JSON.stringify({
        size: bytes.byteLength,
        mediaType: request.mediaType,
      })),
    )
    // 并发发布同一份内容时，先落盘的 metadata 决定 mediaType；报告实际存下来的
    // 引用，而不是本次调用声明的那个。
    const stored = await this.runStat(hash)
    if (!stored) {
      throw new ArtifactError(`artifact metadata disappeared after writing: ${hash}`, 'ARTIFACT_FAILED')
    }
    return stored
  }

  protected override async runGet(hash: string): Promise<Uint8Array> {
    try {
      return await readFile(join(this.contentDir(hash), hash))
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code === 'ENOENT') {
        throw new ArtifactError(`artifact not found: ${hash}`, 'ARTIFACT_NOT_FOUND')
      }
      throw new ArtifactError(`could not read artifact ${hash}`, 'ARTIFACT_FAILED', { cause: error })
    }
  }

  protected override async runStat(hash: string): Promise<ArtifactRef | undefined> {
    const file = join(this.contentDir(hash), hash)
    let meta: string
    try {
      meta = await readFile(`${file}.meta.json`, 'utf8')
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code === 'ENOENT') return undefined
      throw new ArtifactError(`could not read artifact ${hash}`, 'ARTIFACT_FAILED', { cause: error })
    }
    let parsed: unknown
    try {
      parsed = JSON.parse(meta)
    } catch (error) {
      throw new ArtifactError(
        `artifact metadata is not valid JSON: ${hash}`,
        'ARTIFACT_FAILED',
        { cause: error },
      )
    }
    if (!isRecord(parsed) || typeof parsed.size !== 'number' || typeof parsed.mediaType !== 'string') {
      throw new ArtifactError(`artifact metadata is malformed: ${hash}`, 'ARTIFACT_FAILED')
    }
    return { hash, size: parsed.size, mediaType: parsed.mediaType }
  }

  private contentDir(hash: string): string {
    return join(this.root, hash.slice(0, 2))
  }

  /**
   * 同目录内的改名是原子的；崩在中间只会留下 `.tmp`，读者看不到半份内容。并发写入同一
   * 哈希时两个临时文件内容相同，目标已经就位就说明另一个写入者赢了，这次写入算成功。
   */
  private async writeAtomic(path: string, bytes: Uint8Array): Promise<void> {
    const temp = `${path}.${randomUUID()}.tmp`
    try {
      await writeFile(temp, bytes, { flag: 'wx' })
      await this.commit(temp, path)
    } catch (error) {
      throw new ArtifactError(`could not write artifact ${path}`, 'ARTIFACT_FAILED', { cause: error })
    } finally {
      // 改名成功后临时名已经不存在；输掉并发时它仍在，别留下垃圾。
      await rm(temp, { force: true }).catch(() => {})
    }
  }

  /**
   * 把临时文件换成正式文件。目标已经存在时直接成功：路径由内容哈希决定，先到的
   * 那一份内容相同。Windows 不能替换另一个线程正在替换的目标，先短暂重试，再接受
   * 「已经就位」——否则并发发布相同内容会把输掉竞争的一方报成 `EPERM`。
   */
  private async commit(temp: string, path: string): Promise<void> {
    for (let attempt = 0; ; attempt += 1) {
      try {
        await rename(temp, path)
        return
      } catch (error) {
        if (await isFile(path)) return
        const code = (error as NodeJS.ErrnoException).code
        if (!code || !RETRYABLE_RENAME_CODES.has(code) || attempt >= RENAME_RETRIES) throw error
        await delay(10 * 2 ** attempt)
      }
    }
  }
}

export const artifactLocal = {
  name: 'artifact-local',
  apply(ctx: Context, config: LocalArtifactConfig): void {
    new LocalArtifactStore(ctx, config)
  },
}
